import { api } from "../../AxiosInstance";
import { IUser } from "../../interfaces/user.interface";
import rbacUtils from "../../utils/rbacUtils";

export type FleetStatus = "running" | "idle" | "halted" | "unknown";
export interface FleetTelemetry {
  locationSource: "EIM" | "DRIVER_APP" | "UNAVAILABLE";
  metricsSource: "EIM" | "UNAVAILABLE";
  currentLat?: number; currentLong?: number; timestamp?: number;
  fetchedAt?: number; metricsTimestamp?: number; stale: boolean; metricsStale?: boolean; demo?: boolean;
  speed?: number; heading?: number; battery?: number; odometer?: number;
  motorTemperature?: number; wheelSpeed?: number; vehicleState?: string;
  keyState?: number; brakeState?: number; vcuFaultLevel?: number;
  vcuStrategyFaultLevel?: number; mcuErrorLevel?: number; motorFault?: number;
  altitude?: number; satellites?: number; gpsAccuracy?: number;
}
export interface FleetBaseStation {
  id: number; name: string; address?: string; isActive?: boolean;
  latitude?: number; longitude?: number;
}
export interface FleetVehicle {
  id: string; truckId?: number; plate: string; driver?: string; phone?: string;
  driverId?: number; tripId?: number; jobId?: number; jobCode?: string;
  container?: string; source?: string; destination?: string;
  sourcePosition?: { lat: number; lng: number }; destinationPosition?: { lat: number; lng: number };
  lat?: number; lng?: number; speed?: number; fixTime?: number;
  unreachable?: boolean; observedAt?: number; telemetry?: FleetTelemetry;
  status: FleetStatus; tripStatus?: string; assignable?: boolean;
  baseStation?: FleetBaseStation; onTrip?: boolean; distanceFromBaseKm?: number;
}
export interface Facility { id: string; name: string; lat: number; lng: number; radius?: number; }
export interface FleetSnapshot { vehicles: FleetVehicle[]; facilities: Facility[]; delivered: number | null; motionAvailable: boolean; warnings: string[]; updatedAt: number; }
export interface FleetLoadOptions { onInventory?: (snapshot: FleetSnapshot) => void; retryAfter?: Map<number, number>; }
export interface FleetAccess { trucks: boolean; drivers: boolean; tracking: boolean; }

export function validPosition(lat: unknown, lng: unknown): { lat: number; lng: number } | undefined {
  if (lat === null || lng === null || lat === undefined || lng === undefined || lat === "" || lng === "") return;
  const a = Number(lat), b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b) || Math.abs(a) > 90 || Math.abs(b) > 180 || (a === 0 && b === 0)) return;
  return { lat: a, lng: b };
}
export function fleetStatus(hasTrip: boolean, speed?: number, reported?: string): FleetStatus {
  if (hasTrip && reported === "HALTED") return "halted";
  if (speed !== undefined && speed > 0) return "running";
  if (!hasTrip) return "idle";
  // An assigned trip, a stopped fix or a missing fix alone does not prove a halt.
  return "unknown";
}
export function matchesVehicle(vehicle: FleetVehicle, query: string): boolean {
  const search = query.trim().toLowerCase();
  return !search || [vehicle.plate, vehicle.driver, vehicle.jobCode, vehicle.container, vehicle.source, vehicle.destination].some(value => value?.toLowerCase().includes(search));
}

// Trip state is independent of movement: an off-trip truck may still be moving.
export function baseDistanceLabel(vehicle: FleetVehicle): string | undefined {
  if (!vehicle.baseStation || vehicle.baseStation.isActive === false || vehicle.onTrip !== false || vehicle.tripId != null) return;
  const distance = vehicle.distanceFromBaseKm;
  if (vehicle.unreachable || distance == null || !Number.isFinite(distance) || distance < 0) return "Unavailable";
  return distance.toLocaleString("en-IN", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + " km";
}

function payload(response: any): any {
  const body = response?.data;
  if (body?.error) throw new Error(body.message || "Request failed");
  return body?.data ?? body;
}
async function collection(url: string, signal: AbortSignal, params: Record<string, unknown> = {}, onPage?: (items: any[]) => void) {
  const items: any[] = [];
  let partial = false, skip = 0;
  const seen = new Set<string>();
  for (let page = 0; page < 20; page++) {
    const data = payload(await api.get(url, { params: { ...params, skip, limit: 100 }, signal, timeout: 12000 }));
    const rows = Array.isArray(data) ? data : data?.content;
    if (!Array.isArray(rows)) throw new Error("Unexpected list response");
    const fresh = rows.filter(row => {
      const key = row.id ?? row.jobBreakupDetailId;
      if (key === null || key === undefined) return true;
      const identity = String(key);
      if (seen.has(identity)) return false;
      seen.add(identity); return true;
    });
    if (rows.length && !fresh.length) return { items, partial: true };
    items.push(...fresh);
    onPage?.([...items]);
    skip += rows.length;
    const total = data?.page?.totalElements ?? data?.totalElements;
    if (Array.isArray(data) || rows.length === 0 || (typeof total === "number" && items.length >= total) || (total === undefined && rows.length < 100)) return { items, partial };
    partial = page === 19;
  }
  return { items, partial };
}

export async function loadFleet(user: IUser, access: FleetAccess, signal: AbortSignal, previous?: FleetSnapshot | null, options: FleetLoadOptions = {}): Promise<FleetSnapshot> {
  const warnings: string[] = [];
  const sources = [
    access.trucks ? collection(rbacUtils.getRoleBasedEndpoint(user, "/trucks"), signal, {}, items => {
      if (signal.aborted) return;
      options.onInventory?.({ vehicles: items.map(truck => {
        const id = String(truck.id ?? truck.registrationNumber);
        const old = access.tracking ? previous?.vehicles.find(vehicle => vehicle.id === id && vehicle.plate === truck.registrationNumber) : undefined;
        return { id, truckId: truck.id, plate: truck.registrationNumber || truck.name || "Unregistered vehicle",
          ...validPosition(old?.lat, old?.lng), fixTime: old?.fixTime, observedAt: old?.observedAt,
          unreachable: true, status: "unknown", assignable: false };
      }), facilities: [], delivered: null, motionAvailable: false,
        warnings: ["Loading remaining fleet records and trip details…"], updatedAt: Date.now() });
    }) : Promise.resolve({ items: [], partial: false }),
    access.drivers ? collection(rbacUtils.getRoleBasedEndpoint(user, "/drivers"), signal) : Promise.resolve({ items: [], partial: false }),
    collection("/job/active-trips", signal),
    collection("/location", signal),
  ];
  const results = await Promise.allSettled(sources);
  if (signal.aborted) throw new Error("Aborted");
  const labels = ["Fleet records", "Driver records", "Active trips", "Facilities"];
  const lists = results.map((result, index) => {
    if (result.status === "rejected") { warnings.push(labels[index] + " unavailable"); return [] as any[]; }
    if (result.value.partial) warnings.push(labels[index] + " may be incomplete (" + result.value.items.length + " records loaded)");
    return result.value.items;
  });
  if (!access.trucks) warnings.push("Fleet master access is unavailable for your role; showing vehicles from accessible trips");
  const [trucks, drivers, trips, locations] = lists;
  const vehicles = new Map<string, FleetVehicle>();
  const truckTelemetry = new Map<string, FleetTelemetry>();
  for (const truck of trucks) {
    const id = String(truck.id ?? truck.registrationNumber);
    if (access.tracking && truck.telemetry) truckTelemetry.set(id, truck.telemetry);
    vehicles.set(id, { id, truckId: truck.id, plate: truck.registrationNumber || truck.name || "Unregistered vehicle",
      baseStation: truck.baseStation ?? undefined,
      onTrip: typeof truck.onTrip === "boolean" ? truck.onTrip : results[2].status === "fulfilled" && !results[2].value.partial ? false : undefined,
      distanceFromBaseKm: access.tracking && typeof truck.distanceFromBaseKm === "number" && Number.isFinite(truck.distanceFromBaseKm) && truck.distanceFromBaseKm >= 0 ? truck.distanceFromBaseKm : undefined,
      assignable: !["INACTIVE", "MAINTENANCE", "RETIRED"].includes(truck.status), status: "idle" });
  }
  for (const trip of trips) {
    const truckId = trip.truckIdName?.id;
    const id = String(truckId ?? "trip-" + trip.jobBreakupDetailId);
    const existing = vehicles.get(id);
    vehicles.set(id, {
      ...existing, id, truckId, plate: existing?.plate || trip.truckIdName?.name || "Unassigned vehicle",
      driver: trip.driverIdName?.name, driverId: trip.driverIdName?.id,
      tripId: trip.jobBreakupDetailId, jobId: trip.jobDetails?.jobId, jobCode: trip.jobDetails?.jobCode,
      container: trip.containerNumber, source: trip.jobDetails?.source, destination: trip.jobDetails?.destination,
      sourcePosition: validPosition(trip.jobDetails?.sourceLatitude, trip.jobDetails?.sourceLongitude),
      destinationPosition: validPosition(trip.jobDetails?.destLatitude, trip.jobDetails?.destLongitude),
      tripStatus: trip.tripStatus, onTrip: true, distanceFromBaseKm: undefined, status: "unknown",
    });
  }
  for (const driver of drivers) {
    const truckId = driver.currentTruckIdName?.id ?? driver.truckId;
    if (truckId === null || truckId === undefined) continue;
    const id = String(truckId), vehicle = vehicles.get(id);
    if (vehicle) vehicles.set(id, { ...vehicle, driver: vehicle.driver || driver.driverName, driverId: vehicle.driverId || driver.id, phone: driver.driverMobileNumber });
  }
  const facilities: Facility[] = locations.flatMap(location => {
    const position = validPosition(location.latitude, location.longitude);
    return position ? [{ id: String(location.id), name: location.name || location.label || "Facility", ...position, radius: Number(location.radius) > 0 ? Number(location.radius) : undefined }] : [];
  });
  const delivered = null;
  // Publish inventory before telemetry: missing GPS must never hide fleet records.
  options.onInventory?.({ vehicles: Array.from(vehicles.values()).map(vehicle => {
    const old = access.tracking ? previous?.vehicles.find(item => item.id === vehicle.id && item.plate === vehicle.plate) : undefined;
    return { ...vehicle, ...validPosition(old?.lat, old?.lng), fixTime: old?.fixTime, observedAt: old?.observedAt, unreachable: true,
      status: vehicle.tripId || results[2].status === "rejected" || vehicle.assignable === false ? "unknown" : "idle" };
  }), facilities, delivered, motionAvailable: false, warnings: [...warnings], updatedAt: Date.now() });
  // This backend caches GPS for active-trip drivers. Unassigned fleet records do
  // not have a location entry; querying all of them produces waves of 404s.
  const eligible = Array.from(vehicles.values()).filter(vehicle => !truckTelemetry.has(vehicle.id) &&
    (vehicle.tripId || previous?.vehicles.some(old => old.id === vehicle.id && old.lat !== undefined)));
  const retryAfter = options.retryAfter ?? new Map<number, number>();
  const ids = Array.from(new Set(eligible.map(v => v.driverId).filter((id): id is number => typeof id === "number")))
    .filter(id => (retryAfter.get(id) ?? 0) <= Date.now())
    .sort((a, b) => (retryAfter.get(a) ?? 0) - (retryAfter.get(b) ?? 0));
  const fixes = new Map<number, any>();
  let cursor = 0, failed = 0;
  const gpsAbort = new AbortController();
  const cancelGPS = () => gpsAbort.abort();
  signal.addEventListener("abort", cancelGPS, { once: true });
  // One slow or missing GPS service cannot hold a refresh open indefinitely.
  const deadline = setTimeout(cancelGPS, 12000);
  try {
    if (access.tracking) {
      await Promise.all(Array.from({ length: Math.min(6, ids.length) }, async () => {
        while (cursor < ids.length && !gpsAbort.signal.aborted && !signal.aborted) {
          const id = ids[cursor++];
          retryAfter.set(id, Date.now() + 30000);
          try {
            const fix = payload(await api.get("/job/driver-location", { params: { driverId: id }, signal: gpsAbort.signal, timeout: 8000 }));
            fixes.set(id, fix);
            const rawTime = fix?.timestamp ?? fix?.updatedAt;
            const time = typeof rawTime === "number" ? rawTime : rawTime ? Date.parse(rawTime) : undefined;
            if (!validPosition(fix?.currentLat, fix?.currentLong) || (time && time < Date.now() - 300000)) {
              retryAfter.set(id, Date.now() + 300000);
            }
          } catch {
            failed++;
            retryAfter.set(id, Date.now() + 300000);
          }
        }
      }));
    }
  } finally {
    clearTimeout(deadline);
    signal.removeEventListener("abort", cancelGPS);
  }
  if (signal.aborted) throw new Error("Aborted");
  if (failed) warnings.push("Some current locations are unavailable; last known positions are retained");
  if (access.tracking && cursor < ids.length) warnings.push("Remaining GPS updates will continue on the next refresh");
  if (!access.tracking) warnings.push("Live tracking access is unavailable for your role");
  for (const [id, vehicle] of Array.from(vehicles.entries())) {
    const driverFix = vehicle.driverId === undefined ? undefined : fixes.get(vehicle.driverId);
    const telemetry: FleetTelemetry | undefined = truckTelemetry.get(id) ?? driverFix?.telemetry;
    const fix = telemetry ? { ...telemetry, speed: telemetry.speed } : driverFix;
    const point = validPosition(fix?.currentLat, fix?.currentLong);
    const rawSpeed = fix?.speed;
    const speed = rawSpeed !== null && rawSpeed !== undefined && rawSpeed !== "" && Number.isFinite(Number(rawSpeed)) && Number(rawSpeed) >= 0 ? Number(rawSpeed) : undefined;
    const rawTime = fix?.timestamp ?? fix?.updatedAt;
    const parsedTime = rawTime ? (typeof rawTime === "number" ? rawTime : Date.parse(rawTime)) : undefined;
    const fixTime = parsedTime && Number.isFinite(parsedTime) ? parsedTime : undefined;
    const unreachable = !point || (telemetry ? telemetry.stale || !fixTime
      : Boolean(fixTime && fixTime < Date.now() - 300000));
    const old = access.tracking ? previous?.vehicles.find(item => item.id === id && item.plate === vehicle.plate) : undefined;
    // Keep an observed truck position when its current driver fix disappears.
    // Never substitute a facility, trip endpoint or made-up coordinate.
    const lastPoint = point || validPosition(old?.lat, old?.lng);
    vehicles.set(id, {
      ...vehicle, ...lastPoint, unreachable, telemetry,
      observedAt: point ? Date.now() : old?.observedAt,
      fixTime: point ? fixTime : old?.fixTime,
      speed: unreachable || telemetry?.metricsStale ? undefined : speed,
      status: ((results[2].status === "rejected" || vehicle.assignable === false) && speed === undefined) || (unreachable && Boolean(vehicle.tripId))
        ? "unknown" : fleetStatus(Boolean(vehicle.tripId), unreachable || telemetry?.metricsStale ? undefined : speed, unreachable ? undefined : fix?.status),
    });
  }
  if (Array.from(truckTelemetry.values()).some(telemetry => telemetry.demo)) warnings.push("EIM sandbox: vehicle telemetry contains test values");
  return { vehicles: Array.from(vehicles.values()), facilities, delivered, motionAvailable: Array.from(vehicles.values()).some(vehicle => !vehicle.unreachable && vehicle.speed !== undefined) || Array.from(fixes.values()).some(fix => fix?.speed !== null && fix?.speed !== undefined && fix?.speed !== "" && Number.isFinite(Number(fix.speed)) || fix?.status === "HALTED"), warnings, updatedAt: Date.now() };
}
